import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { usePopup } from "@/components/AppPopups";
import { StatusBadge } from "@/components/StatusBadge";
import { MapPicker } from "@/components/MapPicker";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasAnyPermission, hasPermission } from "@/lib/permissions";
import { inr, fmtDate } from "@/lib/format";
import {
  ArrowLeft, Edit, MapPin, Ruler, Lightbulb, Eye, Plus, Ban,
  User, Phone, Copy, Trash2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { BookingCalendar } from "./components/BookingCalendar";
import { DayOptionsModal } from "./components/DayOptionsModal";
import { BookingModal } from "./components/BookingModal";
import { MaintenanceModal } from "./components/MaintenanceModal";
import { BlockHoardingModal } from "./components/BlockHoardingModal";
import { UnblockHoardingModal } from "./components/UnblockHoardingModal";
import { EntryDetailModal } from "./components/EntryDetailModal";
import { HoardingFinancials } from "./components/HoardingFinancials";
import { scheduleType } from "./utils/calendarHelpers";

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className="font-mono font-medium mt-1 text-slate-800">{value}</div>
    </div>
  );
}

function HeroMetric({ label, value, tone = "slate" }) {
  const tones = {
    slate:   "bg-slate-50 border-slate-100 text-slate-900",
    rose:    "bg-rose-50 border-rose-100 text-rose-700",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    blue:    "bg-blue-50 border-blue-100 text-blue-700",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest opacity-60">{label}</div>
      <div className="mt-1 font-mono text-base font-bold">{value}</div>
    </div>
  );
}

function fmtDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function HoardingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const popup = usePopup();
  const [h, setH] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [clients, setClients] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [modal, setModal] = useState(null);

  const canEditAssigned = hasAnyPermission(user, ["edit_assigned_hoardings", "edit_all_hoardings"]);
  const canDelete = hasPermission(user, "delete_hoardings");
  const canCreate = hasPermission(user, "create_hoardings");
  const canSchedule = hasAnyPermission(user, ["update_hoarding_availability", "edit_assigned_hoardings", "edit_all_hoardings"]);
  const canViewClients = hasAnyPermission(user, ["view_own_clients", "view_assigned_clients", "view_all_clients", "create_clients"]);
  const canViewContact = hasPermission(user, "view_client_contact_details");
  const canViewRent = hasAnyPermission(user, ["view_rent_details", "view_profit_and_loss", "view_financial_reports"]);
  const canViewRevenue = hasAnyPermission(user, ["view_basic_revenue", "view_campaign_amounts", "view_profit_and_loss", "view_financial_reports"]);
  const canViewFinancials = hasAnyPermission(user, ["view_basic_revenue", "view_campaign_amounts", "view_rent_details", "view_expenses", "view_profit_and_loss", "view_financial_reports"]);

  const loadHoarding = useCallback(() => api.get(`/hoardings/${id}`, { skipGlobalLoader: true }).then(r => setH(r.data)), [id]);
  const loadSchedule = useCallback(() => api.get(`/hoardings/${id}/schedule`, { skipGlobalLoader: true }).then(r => setSchedule(r.data || [])), [id]);

  useEffect(() => {
    api.get(`/hoardings/${id}/detail`, { skipGlobalLoader: true }).then((r) => {
      setH(r.data.hoarding);
      setSchedule(r.data.schedule || []);
      if (canViewClients) setClients(r.data.clients || []);
    }).catch(() => {
      loadHoarding();
      loadSchedule();
    });
  }, [id, loadHoarding, loadSchedule, canViewClients]);

  async function handleSaveEntry(payload) {
    try {
      await api.post(`/hoardings/${id}/schedule`, payload);
      toast.success(scheduleType(payload) === "booking" ? "Booking added" : "Maintenance scheduled");
      await Promise.all([loadHoarding(), loadSchedule()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
      throw e;
    }
  }

  async function handleDeleteEntry(entryId) {
    try {
      await api.delete(`/hoardings/${id}/schedule/${entryId}`);
      toast.success("Entry removed");
      await Promise.all([loadHoarding(), loadSchedule()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
      throw e;
    }
  }

  async function handleDeleteHoarding() {
    const ok = await popup.confirm({
      title: `Delete hoarding ${h.hoarding_id}?`,
      description: "Financial snapshots and historical financial data will be kept.",
      confirmText: "Delete Hoarding",
      tone: "danger",
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await api.delete(`/hoardings/${id}`);
      toast.success("Hoarding deleted");
      nav("/hoardings");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  }

  async function handleBlockHoarding(payload) {
    try {
      const { data } = await api.post(`/hoardings/${id}/block`, payload);
      setH(data);
      toast.success("Hoarding blocked");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to block hoarding");
      throw e;
    }
  }

  async function handleUnblockHoarding() {
    try {
      const { data } = await api.post(`/hoardings/${id}/unblock`);
      setH(data);
      toast.success("Hoarding unblocked");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to unblock hoarding");
    }
  }

  function handleDayClick(date, entry) {
    if (entry) setModal({ kind: "entry", entry });
    else if (canSchedule) setModal({ kind: "day-options", date });
  }

  const closeModal = () => setModal(null);

  if (!h) return (
    <Layout title="Hoarding">
      <div className="space-y-5 animate-pulse">
        <div className="h-80 rounded-2xl bg-slate-200" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <div className="h-40 rounded-2xl bg-slate-200" />
            <div className="h-56 rounded-2xl bg-slate-200" />
          </div>
          <div className="space-y-5">
            <div className="h-24 rounded-2xl bg-slate-200" />
            <div className="h-32 rounded-2xl bg-slate-200" />
          </div>
        </div>
        <div className="h-48 rounded-2xl bg-slate-200" />
      </div>
    </Layout>
  );

  const heroPhoto = h.photos?.[0]?.url;
  const canEdit = canEditAssigned || (hasPermission(user, "edit_own_hoardings") && h.created_by === user.id);
  const canBlock = canSchedule && h.occupancy_status === "Available";
  const canUnblock = canSchedule && h.occupancy_status === "Blocked";
  const scheduleStats = schedule.reduce((acc, entry) => {
    if (scheduleType(entry) === "booking") acc.bookings += 1;
    if (scheduleType(entry) === "maintenance") acc.maintenance += 1;
    return acc;
  }, { bookings: 0, maintenance: 0 });

  return (
    <Layout
      title={h.name}
      subtitle={`${h.hoarding_id} · ${h.area}, ${h.city}`}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => nav(-1)} className="hx-secondary-action">
            <ArrowLeft size={14} /> Back
          </button>
          {canEdit && (
            <button onClick={() => nav(`/hoardings/${id}/edit`)} data-testid="detail-edit" className="hx-primary-action">
              <Edit size={14} /> Edit
            </button>
          )}
          {canBlock && (
            <button onClick={() => setModal({ kind: "block" })} className="inline-flex items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-100">
              <Ban size={14} /> Block
            </button>
          )}
          {canUnblock && (
            <button onClick={() => setModal({ kind: "unblock" })} className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-100">
              <CheckCircle2 size={14} /> Unblock
            </button>
          )}
          {canCreate && (
            <button onClick={() => nav("/hoardings/new", { state: { template: h } })} className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition-colors hover:bg-red-100">
              <Copy size={14} /> Duplicate
            </button>
          )}
          {canDelete && (
            <button onClick={handleDeleteHoarding} disabled={deleting} data-testid="detail-delete" className="hx-danger-action">
              <Trash2 size={14} /> {deleting ? "Deleting..." : "Delete"}
            </button>
          )}
        </div>
      }
    >
      <div className="hx-hoarding-detail-page space-y-5" data-testid="hoarding-detail">
        {/* Hero card */}
        <div className="hx-card hx-detail-hero overflow-hidden border border-slate-200/80 shadow-sm">
          <div className={heroPhoto ? "grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px]" : "grid grid-cols-1"}>
            <div className="hx-detail-hero-content p-6 sm:p-7">
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge status={h.occupancy_status} />
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{h.type}</span>
              </div>

              <div className="mt-5 max-w-3xl">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{h.hoarding_id}</div>
                <h2 className="mt-1 font-display text-2xl sm:text-3xl font-semibold text-slate-950 leading-tight">{h.name}</h2>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1.5"><MapPin size={15} className="text-slate-400" />{h.area}, {h.city}</span>
                  <span className="inline-flex items-center gap-1.5"><Ruler size={15} className="text-slate-400" />{h.width}ft × {h.height}ft</span>
                  <span className="inline-flex items-center gap-1.5"><Eye size={15} className="text-slate-400" />{h.sqft} sqft</span>
                </div>
              </div>

              <div className="hx-detail-metrics mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
                {canViewRevenue && <HeroMetric label="Display Rate" value={inr(h.monthly_price)} tone="blue" />}
                {canViewRent && <HeroMetric label="Monthly Rent" value={inr(h.monthly_rent || 0)} />}
                <HeroMetric label="Bookings" value={scheduleStats.bookings} tone="emerald" />
                <HeroMetric label="Maintenance" value={scheduleStats.maintenance} tone="blue" />
              </div>
              {h.occupancy_status === "Blocked" && (
                <div className="mt-5 rounded-xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-900">
                  <div className="font-semibold">Blocked until {fmtDateTime(h.blocked_until)}</div>
                  {h.blocked_reason && <div className="mt-1 text-amber-800">{h.blocked_reason}</div>}
                </div>
              )}
            </div>

            {heroPhoto && (
              <div className="relative min-h-[220px] bg-slate-100 xl:min-h-full">
                <img src={heroPhoto} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/30 via-transparent to-transparent" />
              </div>
            )}
          </div>
        </div>

        <div className="hx-detail-section-nav -mx-1 overflow-x-auto rounded-lg border border-slate-200 bg-white/95 p-1 shadow-sm backdrop-blur">
          <div className="flex items-center gap-1">
            {[
              ["#booking", "Booking"],
              ["#financials", "Financials"],
              ["#specs", "Specs"],
              ["#client", "Client"],
              ["#location", "Location"],
              ["#photos", "Photos"],
            ].map(([href, label]) => (
              <a key={href} href={href} className="rounded-md px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-blue-50 hover:text-blue-700">
                {label}
              </a>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div className="hx-detail-main-grid grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-5">
          <div className="space-y-5">
            <section id="booking" className="scroll-mt-28">
              <BookingCalendar
                schedule={schedule}
                onDayClick={handleDayClick}
                onAddBooking={canSchedule ? () => setModal({ kind: "booking" }) : null}
                onAddMaintenance={canSchedule ? () => setModal({ kind: "maintenance" }) : null}
              />
            </section>

            {canViewFinancials && <section id="financials" className="scroll-mt-28"><HoardingFinancials hoardingId={id} user={user} /></section>}

            <section id="specs" className="hx-card scroll-mt-28 p-6 border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-slate-950">Specifications</h3>
                <span className="text-xs text-slate-500">{h.type}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Spec icon={Ruler} label="Size" value={`${h.width}ft × ${h.height}ft`} />
                <Spec icon={Eye} label="Sqft" value={h.sqft} />
                <Spec icon={MapPin} label="Facing" value={h.facing || "—"} />
                <Spec icon={Lightbulb} label="Lighting" value={h.lighting ? "Yes" : "No"} />
                <Spec label="Type" value={h.type} />
                {canViewRent && <Spec label="Monthly Rent" value={inr(h.monthly_rent || 0)} />}
              </div>
              {h.notes && (
                <div className="mt-4 p-4 rounded-xl bg-slate-50 text-sm text-slate-600 border border-slate-100">{h.notes}</div>
              )}
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {h.client_name && (
              <section id="client" className="hx-card scroll-mt-28 p-6 space-y-4 border border-slate-200/80 shadow-sm">
                <h3 className="font-display font-semibold text-slate-950">Current Client</h3>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                    <User size={18} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{h.client_name}</div>
                    {canViewContact && h.client_mobile && <div className="text-xs font-mono text-slate-500">{h.client_mobile}</div>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-100">
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <div className="text-xs text-slate-500">Start</div>
                    <div className="text-sm font-mono font-medium">{fmtDate(h.campaign_start)}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <div className="text-xs text-slate-500">End</div>
                    <div className="text-sm font-mono font-medium">{fmtDate(h.campaign_end)}</div>
                  </div>
                </div>
              </section>
            )}

            <section id="location" className="hx-card scroll-mt-28 p-6 space-y-3 border border-slate-200/80 shadow-sm">
              <h3 className="font-display font-semibold text-slate-950">Address</h3>
              <div className="text-sm text-slate-600">{h.address || "—"}</div>
              <div className="text-xs text-slate-500"><MapPin size={11} className="inline mr-1" />{h.area}, {h.city}</div>
              {h.landmark && <div className="text-xs text-slate-500">Landmark: {h.landmark}</div>}
            </section>

            {h.latitude && h.longitude && (
              <div className="hx-card p-6 border border-slate-200/80 shadow-sm">
                <h3 className="font-display font-semibold text-slate-950 mb-3">Location</h3>
                <MapPicker value={{ lat: h.latitude, lng: h.longitude }} onChange={() => {}} height={260} />
                <div className="text-xs text-slate-500 mt-2">{h.address}</div>
              </div>
            )}
          </div>
        </div>

        {/* Photo gallery */}
        {h.photos?.length ? (
          <section id="photos" className="hx-card scroll-mt-28 p-6 border border-slate-200/80 shadow-sm">
            <h3 className="font-display font-semibold text-slate-950 mb-4">Photos</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {h.photos.map((p, i) => (
                <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 shadow-sm border border-slate-200/60">
                  <img src={p.url} alt="" className="w-full h-full object-cover hover:scale-105 transition duration-700" />
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>

      {/* Modals */}
      {canSchedule && modal?.kind === "day-options" && (
        <DayOptionsModal
          date={modal.date}
          onClose={closeModal}
          onBooking={() => setModal({ kind: "booking", defaultDate: modal.date })}
          onMaintenance={() => setModal({ kind: "maintenance", defaultDate: modal.date })}
        />
      )}
      {modal?.kind === "booking" && (
        <BookingModal clients={clients} defaultDate={modal.defaultDate} onClose={closeModal} onSave={handleSaveEntry} />
      )}
      {modal?.kind === "maintenance" && (
        <MaintenanceModal defaultDate={modal.defaultDate} onClose={closeModal} onSave={handleSaveEntry} />
      )}
      {modal?.kind === "block" && (
        <BlockHoardingModal onClose={closeModal} onSave={handleBlockHoarding} />
      )}
      {modal?.kind === "unblock" && (
        <UnblockHoardingModal hoarding={h} onClose={closeModal} onConfirm={handleUnblockHoarding} />
      )}
      {modal?.kind === "entry" && (
        <EntryDetailModal entry={modal.entry} onClose={closeModal} onDelete={handleDeleteEntry} />
      )}
    </Layout>
  );
}
